import React, {
	memo,
	useCallback,
	useMemo,
	useState,
	useRef,
	useEffect,
} from "react";
import Surface from "./Surface";
import {
	SurfaceActionBarItem,
	SurfaceRoundedCard,
	SurfaceTitle,
} from "./SurfacesCoreUI";

import REWARD_SURFACE_IMAGE from "../../img/reward-surface.png";

import "./reward-surface.css";
import GET from "../../utils/GET";
import calcUserRewardPoints from "../../utils/calcUserRewardPoints";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import { Badge, Input, PostPurchaseExtensionPreview } from "../CoreUI";
import anim from "../../utils/anim";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdCheckmark } from "react-icons/io";
import inExtensionPreview from "../../utils/inExtensionPreview";
import { BrandBubbles, BrandBubblesLinear } from "../BrandBubbles";
import inPreview from "../../utils/inPreview";
import useData from "../../hooks/useData";
import useReward from "../../hooks/useReward";
import accessibilityProvider from "../../utils/accessibilityProvider";

const calcPoints = (userPoints) => {
	let discountedPoints = 0;
	if (userPoints < 10) {
		discountedPoints = userPoints / 5;
	} else if (userPoints < 20) {
		discountedPoints = userPoints / 2;
	} else if (userPoints < 50) {
		discountedPoints = (2 * userPoints) / 3;
	} else if (userPoints < 100) {
		discountedPoints = (3.5 * userPoints) / 4;
	} else {
		discountedPoints = userPoints - 30;
	}
	return Math.floor(discountedPoints);
};

const generateBubblesOnly = GET("generateBubblesOnly") || "";

const RewardSurface = memo(
	({ surfaceData, idx, handleSurfaceClose, showFullCard }) => {
		const {
			opted_in: userOptedIn = false,
			module_order,
			// total_points: userTotalPoints = userPoints,
		} = useMemo(
			() => ({
				...surfaceData,
			}),
			[surfaceData]
		);

		const data = useData();

		const {
			email = false,
			name = false,
			cost,
		} = useMemo(() => data?.scrapedInformation || {}, [data]);

		const userPoints = useMemo(() => calcUserRewardPoints(cost), [cost]);

		const [proceed, setProceed] = useState(false);
		const [showEmailInput, setShowEmailInput] = useState(false);
		const [points, setPoints] = useState(calcPoints(userPoints));
		const [hidePoints, setHidePoints] = useState(false);

		const [trackedActionBarClick, setTrackedActionBarClick] =
			useState(false);

		const pointInterval = useRef(null);
		const track = useTrack();

		const {
			setRewardData,
			remoteActions: [, savePoints],
		} = useReward();

		const rewardResponseTrigger = useCallback(
			(inputEmail = false) => {
				if (inputEmail) {
					setRewardData((rewardData) => ({
						...rewardData,
						email: inputEmail,
					}));
				}

				savePoints();
			},
			[setRewardData, savePoints]
		);

		const handleActionBarClick = useCallback(() => {
			if (!trackedActionBarClick) {
				track(eventTypes.WIDGET_SURFACE_REWARD_CLICK, {
					points_earned: userPoints,
					module_position: idx,
					module_order,
				});
				setTrackedActionBarClick(true);
			}
			setHidePoints(true);
			if (!email) {
				return setShowEmailInput(true);
			}
			if (!inPreview) {
				rewardResponseTrigger();
			}
			setProceed(true);
		}, [
			rewardResponseTrigger,
			track,
			idx,
			email,
			module_order,
			userPoints,
			trackedActionBarClick,
		]);

		const handleClose = useCallback(() => {
			handleSurfaceClose();
		}, [handleSurfaceClose]);

		const handleUserEmailInput = useCallback(
			(value) => {
				track(eventTypes.WIDGET_SURFACE_REWARD_EMAIL_SUBMIT, {
					email: value,
					module_position: idx,
					module_order,
				});
				if (!inPreview) {
					rewardResponseTrigger(value);
				}
				setProceed(true);
			},
			[rewardResponseTrigger, track, idx, module_order]
		);

		const initPointCounter = useCallback(
			(interval) => {
				pointInterval.current = setInterval(() => {
					if (points >= userPoints) {
						clearInterval(pointInterval.current);
						return;
					}
					setPoints((points) => points + 1);
				}, interval);
			},
			[userPoints, points]
		);

		useEffect(() => {
			if (!proceed) {
				return;
			}
			// use this other one while developement
			// const timeToClose = userOptedIn ? 1000 : 3000;
			const timeToClose = 3000;
			// const timeToClose = userOptedIn ? 5000 : 3000;
			setTimeout(() => {
				handleClose();
			}, timeToClose);
		}, [proceed, userOptedIn, handleClose]);

		// useEffect(() => {
		// 	if (!userOptedIn) {
		// 		return;
		// 	}
		// 	setProceed(true);
		// }, [userOptedIn]);

		useEffect(() => {
			const delta = userPoints - points;
			if (delta <= 0) {
				clearInterval(pointInterval.current);
			} else if (delta < 1) {
				clearInterval(pointInterval.current);
				initPointCounter(500);
			} else if (delta < 2) {
				clearInterval(pointInterval.current);
				initPointCounter(350);
			} else if (delta < 3) {
				clearInterval(pointInterval.current);
				initPointCounter(240);
			} else if (delta < 5) {
				clearInterval(pointInterval.current);
				initPointCounter(200);
			} else if (delta < 10) {
				clearInterval(pointInterval.current);
				initPointCounter(100);
			} else if (!pointInterval.current) {
				setTimeout(() => initPointCounter(50), 140);
			}
		}, [userPoints, points, initPointCounter]);

		const imageContent = useMemo(
			() => (
				<section className="reward-surface-image-content">
					<BrandBubbles modalData={surfaceData} data={data} />
				</section>
			),
			[surfaceData, data]
		);

		const imageView = useMemo(
			() => ({
				imgUrl: REWARD_SURFACE_IMAGE,
				renderableContent: imageContent,
				hidePrimaryImg: true,
				showBrandColor: true,
				heightScale: 2.4,
				data,
			}),
			[imageContent, data]
		);

		const actionBar = useMemo(
			() => ({
				props: {
					fitChildren: true,
					small: true,
					variants: anim.rewardActionBar,
					initial: "initial",
					animate: hidePoints || proceed ? "animate" : "",
				},
				children: (
					<SurfaceActionBarItem
						padded
						full
						className="reward-surface-action-bar-item"
						onClick={handleActionBarClick}
					>
						{!proceed ? (
							<>
								<div
									className="reward-surface-action-bar-logo"
									{...accessibilityProvider({
										role: "img",
									})}
								>
									<IoMdCheckmark />
								</div>
								{showEmailInput && (
									<Input
										type="text"
										className="reward-input-field"
										placeholder="Please enter your email address"
										required
										onClick={handleUserEmailInput}
										small
										transparent
										noShadow
									/>
								)}
								{!showEmailInput && <h2>Save your points</h2>}
							</>
						) : (
							<motion.div
								className="reward-surface-action-bar-proceed"
								variants={anim.item}
								initial="initial"
								animate="animate"
							>
								<span
									{...accessibilityProvider({
										role: "img",
										name: "Sent",
									})}
								>
									✅
								</span>{" "}
								We added {userPoints} points to your balance.
							</motion.div>
						)}

						<AnimatePresence>
							{!hidePoints && !proceed && (
								<motion.section
									className="reward-surface-action-bar-points"
									variants={anim.rewardPoints}
									initial="initial"
									animate="animate"
									exit="exit"
								>
									+{userOptedIn ? userPoints : points}
								</motion.section>
							)}
						</AnimatePresence>
					</SurfaceActionBarItem>
				),
			}),
			[
				userPoints,
				points,
				handleActionBarClick,
				hidePoints,
				userOptedIn,
				showEmailInput,
				proceed,
				handleUserEmailInput,
			]
		);

		const displayEvent = useMemo(
			() => ({
				name: eventTypes.WIDGET_SURFACE_REWARD_DISPLAY,
				properties: {
					points_earned: userPoints,
					user_opted_in: userOptedIn,
					module_position: idx,
					module_order,
				},
			}),
			[userPoints, userOptedIn, idx, module_order]
		);

		const skipEvent = useMemo(
			() => ({
				name: eventTypes.WIDGET_SURFACE_REWARD_SKIP,
				properties: {
					points_earned: userPoints,
					user_opted_in: userOptedIn,
					module_position: idx,
					module_order,
				},
			}),
			[userPoints, userOptedIn, idx, module_order]
		);

		if (generateBubblesOnly) {
			return (
				<section
					className={`reward-surface-bubbles-only ${
						generateBubblesOnly !== "linear"
							? "reward-surface-bubbles-only-white"
							: ""
					}`}
				>
					{generateBubblesOnly === "linear" ? (
						<BrandBubblesLinear
							modalData={surfaceData}
							data={data}
						/>
					) : (
						<BrandBubbles modalData={surfaceData} data={data} />
					)}
				</section>
			);
		}

		if (inExtensionPreview) {
			return (
				<PostPurchaseExtensionPreview
					surfaceData={{
						email: email || "your email",
						brandName: data?.p || "this shop",
						points: points,
						bubblesScreenshotUrl:
							surfaceData?.bubbles_screenshot_url || false,
					}}
					type="reward"
					handleClose={handleClose}
				/>
			);
		}

		return (
			<Surface
				handleClose={handleClose}
				imageView={imageView}
				actionBar={actionBar}
				navBar={{ hidden: proceed }}
				displayEvent={displayEvent}
				skipEvent={skipEvent}
				showFullCard={showFullCard}
				offsetTopScale={2.8}
			>
				<SurfaceRoundedCard showFullCard={showFullCard}>
					<SurfaceTitle>You Earned {userPoints} Points</SurfaceTitle>
					<section className="reward-surface-description">
						{!userOptedIn
							? `${
									name ? `${name}, we` : "We"
							  } will send your points
						to ${!email ? "your" : ""}`
							: `Nice to see you again, we sent your points to`}
						<Badge variants={anim.dialogItem}>
							{email || "email address"}
						</Badge>
						<br />
						Points can be spent just like cash at hundreds of other
						shops — including <b>{data?.p}.</b>
					</section>
				</SurfaceRoundedCard>
			</Surface>
		);
	}
);

export default RewardSurface;
